/**
 * Painel de Extração de Entidades
 * Exibe pessoas, lugares, datas, evidências e termos legais identificados
 */

import React, { useState, useEffect, memo } from 'react'
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import HybridAnalysisList from './HybridAnalysisList'
import PersonIcon from '@mui/icons-material/Person'
import BusinessIcon from '@mui/icons-material/Business'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import GavelIcon from '@mui/icons-material/Gavel'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import VerifiedIcon from '@mui/icons-material/Verified'
import LinkIcon from '@mui/icons-material/Link'
import RefreshIcon from '@mui/icons-material/Refresh'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import { EntityExtraction, Entity, EntityType } from '../../../types/transcription'
import { getEntityExtraction, runOnDemandAnalysis } from '../../../services/api'
import AnalysisOutdatedBanner from './AnalysisOutdatedBanner'
import { contextualErrors, FriendlyError } from '../../../services/errorHandler'
import AnalysisNotAvailableCard from './AnalysisNotAvailableCard'
import AnalysisSkippedCard from './AnalysisSkippedCard'
import AnalysisProcessingCard from './AnalysisProcessingCard'

type Props = {
  fileId: string
  onNavigateToSegment?: (segmentIndex: number) => void
  /** Status atual desta análise (do polling) */
  analysisTypeStatus?: string
  /** Callback quando análise on-demand é iniciada */
  onAnalysisStarted?: () => void
  /** Flag indicando que segmentos foram editados após esta análise */
  analysisOutdated?: boolean
}

const ENTITY_CONFIG: Record<EntityType, { label: string; icon: React.ElementType; color: string; bgColor: string }> = {
  PERSON: {
    label: 'Pessoas',
    icon: PersonIcon,
    color: '#1976d2',
    bgColor: '#e3f2fd'
  },
  ORGANIZATION: {
    label: 'Organizações',
    icon: BusinessIcon,
    color: '#7b1fa2',
    bgColor: '#f3e5f5'
  },
  LOCATION: {
    label: 'Locais',
    icon: LocationOnIcon,
    color: '#388e3c',
    bgColor: '#e8f5e9'
  },
  DATE: {
    label: 'Datas',
    icon: CalendarTodayIcon,
    color: '#f57c00',
    bgColor: '#fff3e0'
  },
  EVIDENCE: {
    label: 'Evidências',
    icon: VerifiedIcon,
    color: '#d32f2f',
    bgColor: '#ffebee'
  },
  LEGAL_TERM: {
    label: 'Termos Legais',
    icon: GavelIcon,
    color: '#455a64',
    bgColor: '#eceff1'
  },
  MONEY: {
    label: 'Valores',
    icon: AttachMoneyIcon,
    color: '#2e7d32',
    bgColor: '#e8f5e9'
  }
}

/**
 * Formata o tempo para exibição.
 * Aceita tanto segundos (número ou string numérica) quanto formato HH:MM:SS.
 */
function formatTime(time: string): string {
  if (!time) return '0:00'
  
  // Se já está no formato HH:MM:SS ou MM:SS, retorna diretamente (ou simplificado)
  if (time.includes(':')) {
    const parts = time.split(':')
    if (parts.length === 3) {
      // HH:MM:SS - remover horas se for 00
      const hours = parseInt(parts[0], 10)
      const mins = parseInt(parts[1], 10)
      const secs = parseInt(parts[2], 10)
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    // MM:SS - retorna como está
    return time
  }
  
  // Se for número de segundos
  const seconds = parseFloat(time)
  if (isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Configuração padrão para tipos de entidade desconhecidos
const DEFAULT_ENTITY_CONFIG = {
  label: 'Outros',
  icon: LinkIcon,
  color: '#757575',
  bgColor: '#f5f5f5'
}

/**
 * Converte o formato do backend para o formato esperado pelo frontend.
 * Backend retorna: segments (number[]), context (string), startTime (string)
 * Frontend espera: occurrences (array com segmentIndex, startTime, context)
 */
function buildOccurrencesFromBackend(entity: any): { segmentIndex: number; startTime: string; context: string }[] {
  const segments = entity.segments || []
  const context = entity.context || entity.value || ''
  const startTime = entity.startTime || '0:00'
  
  // Se não há segmentos, criar uma ocorrência padrão
  if (segments.length === 0) {
    return [{ segmentIndex: 0, startTime, context }]
  }
  
  // Criar uma ocorrência para cada segmento
  return segments.map((segmentIndex: number) => ({
    segmentIndex,
    startTime,
    context
  }))
}

function EntityCardBase({
  entity,
  onNavigateToSegment,
  style,
  compact = false
}: {
  entity: Entity
  onNavigateToSegment?: (segmentIndex: number) => void
  style?: React.CSSProperties
  compact?: boolean
}) {
  const config = ENTITY_CONFIG[entity.type] || DEFAULT_ENTITY_CONFIG
  const Icon = config.icon

  // Compatibilidade: backend retorna confidence, frontend espera relevance
  const relevance = (entity as any).confidence ?? entity.relevance ?? 0.5

  // Compatibilidade: backend retorna mentions, frontend espera count
  const count = (entity as any).mentions ?? entity.count ?? 1

  // Compatibilidade: converter formato do backend para occurrences
  const occurrences = entity.occurrences ?? buildOccurrencesFromBackend(entity)

  return (
    <div style={{ ...style, paddingRight: compact ? 4 : 8, paddingBottom: 4 }}>
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e0e0e0',
        borderLeft: compact ? '3px solid' : '4px solid',
        borderLeftColor: config.color,
        height: 'calc(100% - 4px)',
        bgcolor: '#fff',
        '#monkee-file-transcript-client &': { backgroundColor: '#fff !important', background: '#fff !important' },
        '&:hover': {
          boxShadow: 1,
          borderColor: config.color
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: compact ? 1 : 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 1 : 1.5,
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <Box
          sx={{
            width: compact ? 28 : 36,
            height: compact ? 28 : 36,
            borderRadius: compact ? 1 : 1.5,
            bgcolor: config.bgColor,
            '#monkee-file-transcript-client &': { backgroundColor: `${config.bgColor} !important`, background: `${config.bgColor} !important` },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Icon sx={{ color: config.color, fontSize: compact ? 16 : 20 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: compact ? '0.8rem' : '0.95rem', lineHeight: 1.2 }}>
            {entity.value}
          </Typography>
          {entity.normalizedValue && entity.normalizedValue !== entity.value && !compact && (
            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
              → {entity.normalizedValue}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.5 : 1, flexShrink: 0 }}>
          <Chip
            label={`${count}x`}
            size="small"
            sx={{ fontWeight: 600, fontSize: compact ? '0.6rem' : '0.7rem', height: compact ? 18 : 22, bgcolor: config.bgColor, '#monkee-file-transcript-client &': { backgroundColor: `${config.bgColor} !important`, background: `${config.bgColor} !important` }, color: config.color }}
          />
          {!compact && (
            <Tooltip title="Relevância">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {[...Array(5)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 4,
                      height: 12,
                      bgcolor: i < Math.round(relevance * 5) ? config.color : '#e0e0e0',
                      '#monkee-file-transcript-client &': { backgroundColor: `${i < Math.round(relevance * 5) ? config.color : '#e0e0e0'} !important`, background: `${i < Math.round(relevance * 5) ? config.color : '#e0e0e0'} !important` },
                      mx: 0.1,
                      borderRadius: 0.5
                    }}
                  />
                ))}
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Ocorrências - sempre visíveis */}
      <Box sx={{ p: compact ? 1 : 1.5 }}>
        {occurrences.slice(0, compact ? 2 : 3).map((occ, i) => (
          <Box
            key={i}
            sx={{
              p: compact ? 0.75 : 1,
              bgcolor: '#fafafa',
              '#monkee-file-transcript-client &': { backgroundColor: '#fafafa !important', background: '#fafafa !important' },
              borderRadius: 1,
              mb: compact ? 0.5 : 0.75,
              borderLeft: `2px solid ${config.color}`,
              '&:last-child': { mb: 0 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#999', fontSize: compact ? '0.55rem' : '0.65rem', fontFamily: 'monospace' }}>
                  {formatTime(occ.startTime)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', fontSize: compact ? '0.5rem' : '0.6rem' }}>
                  •
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', fontSize: compact ? '0.55rem' : '0.65rem' }}>
                  #{occ.segmentIndex + 1}
                </Typography>
              </Box>
              {onNavigateToSegment && (
                <IconButton
                  size="small"
                  onClick={() => onNavigateToSegment(occ.segmentIndex)}
                  sx={{
                    color: config.color,
                    p: 0.25,
                    '&:hover': { bgcolor: config.bgColor }
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: compact ? 14 : 16 }} />
                </IconButton>
              )}
            </Box>
            <Typography variant="body2" sx={{ color: '#555', fontSize: compact ? '0.65rem' : '0.75rem', lineHeight: 1.4, fontStyle: 'italic' }}>
              "{occ.context}"
            </Typography>
          </Box>
        ))}
        {occurrences.length > (compact ? 2 : 3) && (
          <Typography variant="caption" sx={{ color: '#999', fontSize: compact ? '0.55rem' : '0.65rem', display: 'block', textAlign: 'center', mt: 0.5 }}>
            +{occurrences.length - (compact ? 2 : 3)} mais ocorrências
          </Typography>
        )}
      </Box>
    </Paper>
    </div>
  )
}

// Memoized EntityCard para evitar re-renders desnecessários
const EntityCard = memo(EntityCardBase, (prevProps, nextProps) => {
  return (
    prevProps.entity.id === nextProps.entity.id &&
    prevProps.compact === nextProps.compact &&
    prevProps.onNavigateToSegment === nextProps.onNavigateToSegment
  )
})

function RelationshipCardBase({
  relationship,
  style,
  compact = false
}: {
  relationship: { entity1: string; entity2: string; relationship: string; context: string }
  style?: React.CSSProperties
  compact?: boolean
}) {
  return (
    <div style={{ ...style, paddingRight: compact ? 4 : 8, paddingBottom: 4 }}>
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.25 : 2,
        border: '1px solid #e0e0e0',
        height: 'calc(100% - 4px)',
        bgcolor: '#fafafa',
        '#monkee-file-transcript-client &': { backgroundColor: '#fafafa !important', background: '#fafafa !important' }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 2, mb: compact ? 0.5 : 1, flexWrap: compact ? 'wrap' : 'nowrap' }}>
        <Chip
          label={relationship.entity1}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: compact ? '0.65rem' : '0.8125rem', height: compact ? 22 : 24 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: compact ? 12 : 20, height: 2, bgcolor: '#1976d2', '#monkee-file-transcript-client &': { backgroundColor: '#1976d2 !important', background: '#1976d2 !important' } }} />
          <LinkIcon sx={{ color: '#1976d2', fontSize: compact ? 14 : 18 }} />
          <Box sx={{ width: compact ? 12 : 20, height: 2, bgcolor: '#1976d2', '#monkee-file-transcript-client &': { backgroundColor: '#1976d2 !important', background: '#1976d2 !important' } }} />
        </Box>
        <Chip
          label={relationship.entity2}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: compact ? '0.65rem' : '0.8125rem', height: compact ? 22 : 24 }}
        />
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', mb: 0.5, fontSize: compact ? '0.75rem' : '0.875rem' }}>
        {relationship.relationship}
      </Typography>
      <Typography variant="caption" sx={{ color: '#666', fontSize: compact ? '0.65rem' : '0.75rem', lineHeight: compact ? 1.4 : 1.5 }}>
        {relationship.context}
      </Typography>
    </Paper>
    </div>
  )
}

// Memoized RelationshipCard para evitar re-renders desnecessários
const RelationshipCard = memo(RelationshipCardBase, (prevProps, nextProps) => {
  return (
    prevProps.relationship.entity1 === nextProps.relationship.entity1 &&
    prevProps.relationship.entity2 === nextProps.relationship.entity2 &&
    prevProps.relationship.relationship === nextProps.relationship.relationship &&
    prevProps.compact === nextProps.compact
  )
})

export default function EntityExtractionPanel({ fileId, onNavigateToSegment, analysisTypeStatus, onAnalysisStarted, analysisOutdated }: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [extraction, setExtraction] = useState<EntityExtraction | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [activeType, setActiveType] = useState<EntityType | 'all' | 'relationships'>('all')
  const [entitiesFullscreen, setEntitiesFullscreen] = useState(false)
  const [relationshipsFullscreen, setRelationshipsFullscreen] = useState(false)

  const [reanalyzing, setReanalyzing] = useState(false)

  const handleReanalyze = async () => {
    setReanalyzing(true)
    try {
      await runOnDemandAnalysis(fileId, 'entities')
      onAnalysisStarted?.()
    } finally {
      setReanalyzing(false)
    }
  }

  const loadExtraction = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getEntityExtraction(fileId)
      setExtraction(data)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
      console.error('Erro ao carregar entidades:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (analysisTypeStatus === 'SKIPPED' || analysisTypeStatus === 'PROCESSING') return
    loadExtraction()
  }, [fileId, analysisTypeStatus])

  if (analysisTypeStatus === 'SKIPPED') {
    return (
      <AnalysisSkippedCard
        title="Extração de Entidades"
        analysisType="entities"
        fileId={fileId}
        onAnalysisStarted={onAnalysisStarted}
      />
    )
  }

  if (analysisTypeStatus === 'PROCESSING') {
    return <AnalysisProcessingCard title="Extração de Entidades" />
  }

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
          <CircularProgress size={isMobile ? 36 : 48} />
          <Typography sx={{ mt: 2, color: '#666', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            {isMobile ? 'Extraindo...' : 'Extraindo entidades...'}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa', '--monkee-bg': '#fafafa', '#monkee-file-transcript-client &': { '--monkee-bg': '#fafafa', backgroundColor: '#fafafa !important', background: '#fafafa !important' } }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
          <Box sx={{ fontSize: { xs: 36, sm: 48 }, mb: { xs: 1.5, sm: 2 } }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {error.title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {error.message}
          </Typography>
          {error.canRetry && (
            <Button
              variant="outlined"
              onClick={loadExtraction}
              startIcon={<RefreshIcon />}
              size={isMobile ? 'small' : 'medium'}
            >
              {isMobile ? 'Tentar' : 'Tentar novamente'}
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  if (!extraction) {
    return (
      <AnalysisNotAvailableCard 
        title="Extração de Entidades" 
        onRetry={loadExtraction} 
      />
    )
  }

  // Variáveis seguras com fallbacks para evitar erros de undefined
  const entities = extraction.entities ?? []
  const relationships = extraction.relationships ?? []
  const byType = extraction.byType ?? {} as Record<EntityType, Entity[]>

  const entityTypes = Object.keys(ENTITY_CONFIG) as EntityType[]
  
  // Função helper para obter contagem de entidades por tipo
  const getEntityCountByType = (type: EntityType): number => {
    // Se byType está disponível e tem o tipo, usa
    if (byType[type] && byType[type].length > 0) {
      return byType[type].length
    }
    // Caso contrário, conta manualmente
    return entities.filter(entity => entity.type === type).length
  }
  
  // Filtra entidades por tipo
  // Se byType está disponível e populado, usa ele
  // Caso contrário, filtra manualmente do array entities
  const filteredEntities = activeType === 'all' || activeType === 'relationships'
    ? entities
    : (byType[activeType] && byType[activeType].length > 0)
      ? byType[activeType]
      : entities.filter(entity => entity.type === activeType)

  return (
    <Box>
      {analysisOutdated && (
        <AnalysisOutdatedBanner
          analysisType="entities"
          onReanalyze={handleReanalyze}
          isReanalyzing={reanalyzing}
        />
      )}
      {/* Resumo */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
              🔍 {isMobile ? 'Entidades' : 'Extração de Entidades'}
            </Typography>
            <Tooltip title="Atualizar análise" disableHoverListener={isMobile}>
              <IconButton onClick={loadExtraction} size="small">
                <RefreshIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Cards de contagem por tipo */}
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {entityTypes.map((type) => {
              const config = ENTITY_CONFIG[type]
              const Icon = config.icon
              const count = getEntityCountByType(type)

              return (
                <Grid item xs={4} sm={4} md={3} lg={2} key={type}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1, sm: 2 },
                      textAlign: 'center',
                      border: '1px solid #e0e0e0',
                      bgcolor: activeType === type ? config.bgColor : '#fff',
                      '--monkee-bg': activeType === type ? config.bgColor : '#fff',
                      '#monkee-file-transcript-client &': { '--monkee-bg': activeType === type ? config.bgColor : '#fff', backgroundColor: `${activeType === type ? config.bgColor : '#fff'} !important`, background: `${activeType === type ? config.bgColor : '#fff'} !important` },
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: config.bgColor,
                        borderColor: config.color
                      }
                    }}
                    onClick={() => setActiveType(type)}
                  >
                    <Badge badgeContent={count} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: isMobile ? '0.6rem' : '0.75rem' } }}>
                      <Icon sx={{ fontSize: { xs: 22, sm: 32 }, color: config.color }} />
                    </Badge>
                    <Typography variant="caption" sx={{ display: 'block', mt: { xs: 0.5, sm: 1 }, fontWeight: 500, fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                      {isMobile ? config.label.split(' ')[0] : config.label}
                    </Typography>
                  </Paper>
                </Grid>
              )
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs de Filtro */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 1.5, sm: 2 } }}>
        <Tabs
          value={activeType}
          onChange={(_, v) => { setActiveType(v as EntityType | 'all' | 'relationships'); setEntitiesFullscreen(false); setRelationshipsFullscreen(false) }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: { xs: 36, sm: 48 },
            '& .MuiTab-root': {
              minHeight: { xs: 36, sm: 48 },
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
              py: { xs: 0.5, sm: 1 },
              px: { xs: 1, sm: 2 },
              minWidth: { xs: 'auto', sm: 90 }
            }
          }}
        >
          <Tab value="all" label={isMobile ? `Todas (${entities.length})` : `Todas (${entities.length})`} />
          {entityTypes.map((type) => {
            const count = getEntityCountByType(type)
            if (count === 0) return null
            return (
              <Tab
                key={type}
                value={type}
                label={isMobile ? `${count}` : `${ENTITY_CONFIG[type].label} (${count})`}
                icon={isMobile ? <Box component={ENTITY_CONFIG[type].icon} sx={{ fontSize: 14, color: ENTITY_CONFIG[type].color }} /> : undefined}
                iconPosition="start"
                sx={isMobile ? { minWidth: 'auto', px: 1 } : undefined}
              />
            )
          })}
          <Tab value="relationships" label={isMobile ? `🔗 ${relationships.length}` : `Relacionamentos (${relationships.length})`} />
        </Tabs>
      </Box>

      {/* Lista de Entidades ou Relacionamentos */}
      {activeType === 'relationships' ? (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {isMobile ? `Relacionamentos (${relationships.length})` : 'Relacionamentos Detectados'}
            </Typography>
            <Tooltip title="Abrir em tela cheia">
              <IconButton
                size="small"
                onClick={() => setRelationshipsFullscreen(true)}
                sx={{ color: '#666', '&:hover': { color: '#1976d2' } }}
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <HybridAnalysisList
            items={relationships}
            renderItem={(rel, index) => (
              <RelationshipCard
                relationship={rel}
                compact={isMobile}
              />
            )}
            emptyMessage="Nenhum relacionamento detectado"
            emptySubMessage="Relacionamentos entre entidades aparecerão aqui"
            fullscreenTitle="Relacionamentos Detectados"
            isFullscreenOpen={relationshipsFullscreen}
            onFullscreenClose={() => setRelationshipsFullscreen(false)}
          />
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {activeType === 'all'
                ? (isMobile ? `Entidades (${entities.length})` : `Todas as Entidades (${entities.length})`)
                : `${ENTITY_CONFIG[activeType as EntityType].label} (${filteredEntities.length})`
              }
            </Typography>
            <Tooltip title="Abrir em tela cheia">
              <IconButton
                size="small"
                onClick={() => setEntitiesFullscreen(true)}
                sx={{ color: '#666', '&:hover': { color: '#1976d2' } }}
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <HybridAnalysisList
            items={filteredEntities}
            renderItem={(entity, index) => (
              <EntityCard
                entity={entity}
                onNavigateToSegment={onNavigateToSegment}
                compact={isMobile}
              />
            )}
            emptyMessage={isMobile ? 'Nenhuma entidade' : 'Nenhuma entidade encontrada'}
            emptySubMessage={isMobile ? 'Mude o filtro' : 'Tente selecionar outra categoria de entidade'}
            onClearFilters={() => setActiveType('all')}
            fullscreenTitle={activeType === 'all' ? 'Entidades — Todas' : `Entidades — ${ENTITY_CONFIG[activeType as EntityType]?.label ?? ''}`}
            isFullscreenOpen={entitiesFullscreen}
            onFullscreenClose={() => setEntitiesFullscreen(false)}
          />
        </Box>
      )}

      {/* Legenda - more compact on mobile */}
      <Box sx={{ mt: { xs: 1.5, sm: 2 }, p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f5f5', '#monkee-file-transcript-client &': { backgroundColor: '#f5f5f5 !important', background: '#f5f5f5 !important' }, borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1.5 }}>
          {isMobile ? (
            <>
              💡 Use ➔ para ir à transcrição.
            </>
          ) : (
            <>
              💡 <strong>Dica:</strong> As barras de relevância indicam a importância da entidade no documento (quanto mais barras preenchidas, mais relevante).
              {onNavigateToSegment && ' Use o botão ➔ para ir diretamente ao segmento na aba Transcrição.'}
            </>
          )}
        </Typography>
      </Box>
    </Box>
  )
}
